import { useState } from 'react';

import cookie from 'cookie_js';

import { fdApi } from '../../../../core/api';
import { EnqueueSnackbar } from '../../../../core/state/general/general.types';
import { ProfileActionsType } from '../../../../types/menu';
import { urlUtils } from '../../../../utils';
import { Track } from '../../../../weborder/analytics';
import * as Api from '../../../../weborder/Api';
import {
  CUSTOM_EVENTS,
  publishCustomEvent,
} from '../../../../weborder/CustomEvents';
import * as Data from '../../../../weborder/Data';
import * as Intercom from '../../../../weborder/intercom';
import { CreditCardPaymentAddedProps } from '../../../../weborder/redux/payment/actions';
import { Routes } from '../../../../weborder/Routes';
import { storage } from '../../../../weborder/services/storageServices';
import { IntercomSettings } from '../../../../weborder/Store';

interface ReceivePromotionSetting {
  enabled: boolean;
}

type ProfileActions = {
  customerProfileActions: ProfileActionsType;
  redirectToLogin: () => void;
  showAboutMyData: boolean;
};

export const useProfileActions = (
  actions: {
    cancelAddCardBridge: () => void;
    changeLanguage: (lang: string) => void;
    clearBasketStatus: () => void;
    clearDeliveryLocations: () => void;
    creditCardPaymentAdded: (props: CreditCardPaymentAddedProps) => void;
    deletePaymentMethod: (payload: { paymentId: string }) => void;
    logoutSuccessBridge: () => void;
    pushLoginUrl: (url: string) => void;
    setAfterProfilePanelUrl: (url: string) => void;
    setCustomer: (customer: Data.Async<Api.Customer>) => void;
    setCustomerEmail: (
      customerEmail: string,
      enqueueSnackbar: EnqueueSnackbar,
    ) => void;
    setSmsEnabled: (state: boolean) => void;
    setUserName: (name: string, enqueueSnackbar: EnqueueSnackbar) => void;
  },
  configData: Api.AppConfig,
  collectionSummaries: Data.Async<Api.CollectionRestaurantSummary[]>,
  intercom: IntercomSettings,
  showRestaurantList: boolean,
  params?: Routes['params'],
): ProfileActions => {
  const [showAboutMyData, setShowAboutMyData] = useState(false);

  const toggleAboutMyDataDialog = (): void =>
    setShowAboutMyData(!showAboutMyData);

  const clearCustomerSession = (): void => {
    actions.logoutSuccessBridge();
    actions.setCustomer({ status: 'none' });

    publishCustomEvent({ name: CUSTOM_EVENTS.USER_LOGOUT });
    cookie.remove('flipdish-token');
    storage.removeItem('flipdish-delivery-locations');
    storage.removeItem('flipdish-location');
    storage.removeItem('flipdish-device-id');

    let link = '/where';
    if (!configData.PickupEnabled) {
      link = '/delivery';
    } else if (!configData.DeliveryEnabled) {
      link = urlUtils.getCollectionLink(configData, collectionSummaries);
    } else if (!showRestaurantList) {
      link = `/restaurant/${params?.vrid}`;
    }
    urlUtils.navigateToHash(link);
  };

  const handleContactUs = (): void => {
    Track('Profile', 'Contact Us');
    Intercom.ShowMessages(intercom);
  };

  const handleLogout = (): void => {
    actions.setCustomer({ status: 'pending' });

    fdApi.use(fdApi.user.postAccountLogOff()).then(clearCustomerSession);
    actions.clearDeliveryLocations();
  };

  const handlePromotionsSetting = (state: boolean): void => {
    actions.setSmsEnabled(state);
    const payload: ReceivePromotionSetting = {
      enabled: state,
    };

    fdApi
      .use(fdApi.user.putReceivePromotions(payload))
      .then((data: ReceivePromotionSetting) =>
        actions.setSmsEnabled(data.enabled),
      );
  };

  const redirectToLogin = (): void => {
    const locationHash = urlUtils.getWindowLocation('hash');
    const loginUrl = locationHash.substring(1);
    actions.pushLoginUrl(loginUrl);

    const redirectUrl =
      params?.id && params?.type === 'delivery'
        ? `/phone/${params.id}`
        : 'phone';
    urlUtils.navigateToHash(redirectUrl);
  };

  const handleNavigateToPreviousOrders = (): void => {
    const locationHash = urlUtils.getWindowLocation('hash');
    const isOnPreviousOrderDetails = locationHash.includes('history/');

    if (isOnPreviousOrderDetails) {
      urlUtils.navigateToHash('/history');
    } else {
      actions.setAfterProfilePanelUrl(locationHash.substring(1));
      urlUtils.navigateToHash('/history');
    }
  };

  const customerProfileActions: ProfileActionsType = {
    onAboutMyData: toggleAboutMyDataDialog,
    onAddPaymentCard: actions.creditCardPaymentAdded,
    onCancelAddCardBridge: actions.cancelAddCardBridge,
    onChangeEmail: actions.setCustomerEmail,
    onChangeLanguage: actions.changeLanguage,
    onChangeName: actions.setUserName,
    onChangePromotions: handlePromotionsSetting,
    onClearBasketStatus: actions.clearBasketStatus,
    onContactUs: handleContactUs,
    onDeletePaymentMethod: actions.deletePaymentMethod,
    onLogOut: handleLogout,
    onNavigateToPreviousOrders: handleNavigateToPreviousOrders,
  };

  return {
    customerProfileActions,
    redirectToLogin,
    showAboutMyData,
  };
};
